/**
 * Insert sponsored creatives into an organic Discover grid.
 * Rules: never first, never adjacent sponsored, at most `max`, start after `after` organic cards.
 */
export function insertSponsoredCards<TOrganic, TSponsored>(
  organic: TOrganic[],
  sponsored: TSponsored[],
  options: { after?: number; max?: number } = {}
): Array<{ kind: 'organic'; item: TOrganic } | { kind: 'sponsored'; item: TSponsored }> {
  const after = Math.max(1, options.after ?? 6);
  const max = Math.max(0, options.max ?? 2);
  const creatives = sponsored.slice(0, max);

  if (!organic.length || !creatives.length) {
    return organic.map((item) => ({ kind: 'organic' as const, item }));
  }

  const result: Array<
    { kind: 'organic'; item: TOrganic } | { kind: 'sponsored'; item: TSponsored }
  > = [];

  let creativeIndex = 0;
  let organicCount = 0;
  let lastWasSponsored = false;
  // Next organic index at which we may insert (1-based organic count threshold).
  let nextInsertAt = after;

  for (let i = 0; i < organic.length; i++) {
    result.push({ kind: 'organic', item: organic[i] });
    organicCount += 1;
    lastWasSponsored = false;

    const canInsert =
      creativeIndex < creatives.length &&
      organicCount >= nextInsertAt &&
      !lastWasSponsored &&
      // Never first position overall (organicCount >= after >= 1 ensures this when after >= 1)
      result.length > 0;

    if (canInsert) {
      result.push({ kind: 'sponsored', item: creatives[creativeIndex] });
      creativeIndex += 1;
      lastWasSponsored = true;
      // Space next sponsored: at least one organic between, prefer ~after gap.
      nextInsertAt = organicCount + after;
    }
  }

  return result;
}
